import { createRouter, createWebHistory } from 'vue-router'
import { checkAdminRights, checkHostname } from '@/utils/auth'
import { useSyncStore } from '@/stores/SyncStore'
import { useAuthStore } from '@/stores/AuthStore'
import { useAPIStore } from '@/stores/APIStore'
import { useDebounceFn } from '@vueuse/core'
import { bundleShortcuts } from '@/utils/permissionShortcuts'
import { useUIStore } from '@/stores/UIStore'

const OnboardingLayout = () => import('@/components/Onboarding/Layout.vue')
const OnboardingHome = () => import('@/components/Onboarding/Home.vue')

const SuperUserLayout = () => import('@/components/SuperUser/Layout.vue')
const SuperUserHome = () => import('@/pages/superuser/SuperUserHome.vue')
const SuperUserCreateOrganization = () => import('@/pages/superuser/SuperUserCreateOrganization.vue')
const SuperUserCreateUser = () => import('@/pages/superuser/SuperUserCreateUser.vue')

const AuthLayout = () => import('@/components/AuthLayout.vue')
const Login = () => import('@/pages/auth/Login.vue')
const Logout = () => import('@/pages/auth/Logout.vue')

const Main = () => import('@/components/Main/DefaultContent/Main.vue')
const Home = () => import('@/pages/Home.vue')
const NewLocation = () => import('@/pages/locations/NewLocation.vue')
const NewNetwork = () => import('@/pages/meraki/NewNetwork.vue')
const Switchports = () => import('@/pages/meraki/SwitchPorts.vue')
const LocationDetail = () => import('@/pages/locations/LocationDetail.vue')
const Speedtests = () => import('@/pages/locations/Speedtests.vue')
const SettingsGeneral = () => import('@/pages/settings/organization/General.vue')
const ImportLocations = () => import('@/pages/locations/ImportLocations.vue')
const Notification = () => import('@/pages/notifications/Notification.vue')
const About = () => import('@/pages/about/About.vue')
const MerakiKPN = () => import('@/pages/meraki/CellularBackup.vue')
const SubnetInformation = () => import('@/pages/meraki/SubnetInformation.vue')
const ChangeLog = () => import('@/pages/about/ChangeLog.vue')
const Devices = () => import('@/pages/meraki/Devices.vue')
const Locations = () => import('@/pages/locations/Locations.vue')
const LocationTypes = () => import('@/pages/locations/LocationTypes.vue')
const NewLocationType = () => import('@/pages/locations/NewLocationType.vue')
const SupportDesk = () => import('@/pages/supportdesk/Dashboard.vue')
const AllTickets = () => import('@/pages/supportdesk/AllTickets.vue')
const ChangesCalendar = () => import('@/pages/supportdesk/ChangesCalendar.vue')
const ServiceReports = () => import('@/pages/supportdesk/ServiceReports.vue')
const Profile = () => import('@/pages/settings/account/Profile.vue')
const Clients = () => import('@/pages/meraki/Clients.vue')
const VPNStatus = () => import('@/pages/meraki/VPNStatus.vue')
const InternetConnections = () => import('@/pages/meraki/InternetConnections.vue')
const Settings = () => import('@/components/Main/DefaultContent/Settings.vue')
const Security = () => import('@/pages/settings/organization/Security.vue')
const AccountSecurity = () => import('@/pages/settings/account/AccountSecurity.vue')
const Team = () => import('@/pages/settings/organization/Team.vue')
const Invitations = () => import('@/pages/settings/organization/Invitations.vue')
const MagicLinks = () => import('@/pages/settings/organization/MagicLinks.vue')
const Roles = () => import('@/pages/settings/organization/Roles.vue')
const Integrations = () => import('@/pages/settings/organization/Integrations.vue')
const Upgrade = () => import('@/pages/settings/billing/Upgrade.vue')
const YourPlan = () => import('@/pages/settings/billing/YourPlan.vue')
const Invoices = () => import('@/pages/settings/billing/Invoices.vue')
const BillingDetails = () => import('@/pages/settings/billing/BillingDetails.vue')
const E404 = () => import('@/components/404.vue')
const EditUser = () => import('@/pages/settings/organization/EditUser.vue')
const EditRole = () => import('@/pages/settings/organization/EditRole.vue')
const ServiceReport = () => import('@/pages/supportdesk/ServiceReport.vue')
const NewServiceReport = () => import('@/pages/supportdesk/NewServiceReport.vue')
const LocationsDocumentation = () => import('@/pages/locations/LocationsDocumentation.vue')
const LocationDocumentation = () => import('@/pages/locations/LocationDocumentation.vue')
const Changelogs = () => import('@/pages/about/Changelogs.vue')
const Licenses = () => import('@/pages/about/Licenses.vue')
const Logs = () => import('@/pages/logs/Logs.vue')
const LocationBranches = () => import('@/pages/locations/LocationBranches.vue')
const NewLocationBranch = () => import('@/pages/locations/NewLocationBranch.vue')
const Trouble = () => import('@/pages/auth/Trouble.vue')
const ForgotEmail = () => import('@/pages/auth/ForgotEmail.vue')
const ForgotPassword = () => import('@/pages/auth/ForgotPassword.vue')
const ResetPassword = () => import('@/pages/auth/ResetPassword.vue')
const RequestCameraAccess = () => import('@/pages/meraki/RequestCameraAccess.vue')
const NetworkRanges = () => import('@/pages/ipamBlueprints/NetworkRanges.vue')
const VLANRules = () => import('@/pages/ipamBlueprints/VLANRules.vue')
const VLANRuleActions = () => import('@/pages/ipamBlueprints/VLANRuleActions.vue')
const Networks = () => import('@/pages/meraki/Networks.vue')
const WorldMapVPNStatus = () => import('@/pages/meraki/WorldMapVPNStatus.vue')
const WorldMapVPNStatusFullscreen = () => import('@/pages/meraki/WorldMapVPNStatusFullscreen.vue')
const WorldMapLocationOverviewFullscreen = () => import('@/pages/locations/WorldMapLocationOverviewFullscreen.vue')
const NetworkDetail = () => import('@/pages/meraki/NetworkDetail.vue')
const LocationNetworkBind = () => import('@/pages/locations/LocationNetworkBind.vue')
const NewLocationDocumentation = () => import('@/pages/locations/NewLocationDocumentation.vue')
const NewNetworkRange = () => import('@/pages/ipamBlueprints/NewNetworkRange.vue')
const NewVLANRuleAction = () => import('@/pages/ipamBlueprints/NewVLANRuleAction.vue')
const NewVLANRule = () => import('@/pages/ipamBlueprints/NewVLANRule.vue')
const MerakiDashboard = () => import('@/pages/meraki/Dashboard.vue')
const LocationsDashboard = () => import('@/pages/locations/Dashboard.vue')
const Register = () => import('@/pages/auth/Register.vue')
const SettingsDashboard = () => import('@/pages/settings/Dashboard.vue')
const ImportSubnets = () => import('@/pages/meraki/ImportSubnets.vue')
const GuestWifi = () => import('@/pages/settings/meraki/GuestWifi.vue')
const Overview = () => import('@/pages/ipam/Overview.vue')
const APIKeys = () => import('@/pages/settings/organization/APIKeys.vue')
const NetworkSettings = () => import('@/pages/settings/meraki/Networks.vue')
const Configuration = () => import('@/pages/settings/location/Configuration.vue')
const SupportDeskConfiguration = () => import('@/pages/settings/supportdesk/Configuration.vue')
const LocationGranularPermissions = () => import('@/pages/settings/location/GranularPermissions.vue')
const MicrosoftCallback = () => import('@/pages/auth/MicrosoftCallback.vue')
const SimCards = () => import('@/pages/external-tools/SimCards.vue')
const VerkadaDashboard = () => import('@/pages/verkada/Dashboard.vue')
const VerkadaCameras = () => import('@/pages/verkada/Cameras.vue')
const VerkadaControllerGroup = () => import('@/pages/verkada/ControllerGroups.vue')
const VerkadaSites = () => import('@/pages/verkada/Sites.vue')
const VerkadaRequestCameraAccess = () => import('@/pages/verkada/RequestCameraAccess.vue')
const VerkadaLocationSiteBind = () => import('@/pages/verkada/LocationSiteBind.vue')
const WyebotDashboard = () => import('@/pages/wyebot/Dashboard.vue')
const WyebotSensors = () => import('@/pages/wyebot/Sensors.vue')
const WyebotSites = () => import('@/pages/wyebot/Sites.vue')
const WyebotLocationSiteBind = () => import('@/pages/wyebot/LocationSiteBind.vue')
const WyebotLocationSensorBind = () => import('@/pages/wyebot/LocationSensorBind.vue')
const WyebotCreateNewTest = () => import('@/pages/wyebot/CreateNewTest.vue')
const WyebotTests = () => import('@/pages/wyebot/Tests.vue')
const MXPorts = () => import('@/pages/meraki/MXPorts.vue')
const DocumentationDashboard = () => import('@/pages/documentation/Dashboard.vue')
const DocumentationProcedures = () => import('@/pages/documentation/Procedures.vue')
const NewProcedureDocumentation = () => import('@/pages/documentation/NewProcedureDocumentation.vue')
const ProcedureDocumentation = () => import('@/pages/documentation/ProcedureDocumentation.vue')


const NoComponent = { template: '<div></div>' }


const routes = [
  {
    path: '/',
    name: 'Main',
    component: Main,
    beforeEnter: checkHostname,
    children: [
      {
        path: '',
        name: 'Home',
        component: Home,
      },

      {
        path: 'locations/',
        name: 'Locations',
        component: LocationsDashboard,
        meta: {
          title: 'Locations Dashboard',
          salesPage: 'https://pollux.net/modules/#locations',
          modulePermissions: [[
            "LOCATION",
          ]],
          userPermissions: [
            ['@pollux.view.location.overview'],
            ['@pollux.view.location.type'],
            ['@pollux.view.location.branch'],
            ['@pollux.view.location.speedtest'],
          ],
        },
      },
      {
        path: 'locations/overview',
        name: 'Locations Overview',
        component: Locations,
        meta: {
          title: 'Locations Overview',
          salesPage: 'https://pollux.net/modules/#locations',
          modulePermissions: [[
            "LOCATION",
          ]],
          userPermissions: [[
            "@pollux.view.location.overview"
          ]]
        },
      },
      {
        path: 'locations/documentation',
        name: 'Locations Documentation',
        component: LocationsDocumentation,
        meta: {
          title: 'Location Documentation',
          salesPage: 'https://pollux.net/modules/#locations',
          modulePermissions: [[
            "LOCATION",
            "LOCATION_DOCUMENTATION",
          ]],
          userPermissions: [[
            "@pollux.view.location.documentation"
          ]]
        },
      },
      {
        path: 'locations/documentation/new',
        name: 'New Location Documentation',
        component: NewLocationDocumentation,
        meta: {
          title: 'New Location Documentation',
          salesPage: 'https://pollux.net/modules/#locations',
          modulePermissions: [[
            "LOCATION",
            "LOCATION_DOCUMENTATION:CREATE"
          ]],
          userPermissions: [[
            "@pollux.manage.location.documentation"
          ]]
        },
      },
      {
        path: 'locations/documentation/:id',
        name: 'Location Documentation',
        component: LocationDocumentation,
        meta: {
          title: 'View Location Documentation',
          salesPage: 'https://pollux.net/modules/#locations',
          modulePermissions: [[
            "LOCATION",
            "LOCATION_DOCUMENTATION",
          ]],
          userPermissions: [[
            "@pollux.view.location.documentation"
          ]]
        },
      },
      {
        path: 'locations/new',
        name: 'Add New Location',
        component: NewLocation,
        meta: {
          title: 'New Location',
          salesPage: 'https://pollux.net/modules/#locations',
          modulePermissions: [[
            "LOCATION:CREATE",
          ]],
          userPermissions: [[
            "@pollux.manage.location.location"
          ]]
        },
      },
      {
        path: 'locations/import',
        name: 'Add new locations via CSV',
        component: ImportLocations,
        meta: {
          title: 'Import Locations',
          salesPage: 'https://pollux.net/modules/#locations',
          modulePermissions: [[
            "LOCATION:CREATE",
          ]],
          userPermissions: [[
            "@pollux.import.location.location"
          ]]
        },
      },
      {
        path: 'locations/types',
        name: 'Location Types',
        component: LocationTypes,
        meta: {
          title: 'Location Types',
          salesPage: 'https://pollux.net/modules/#locations',
          modulePermissions: [[
            "LOCATION",
            "LOCATION_TYPE",
          ]],
          userPermissions: [[
            "@pollux.view.location.type"
          ]]
        },
      },
      {
        path: 'locations/types/new',
        name: 'New Location Type',
        component: NewLocationType,
        meta: {
          title: 'New Location Type',
          salesPage: 'https://pollux.net/modules/#locations',
          modulePermissions: [[
            "LOCATION",
            "LOCATION_TYPE:CREATE",
          ]],
          userPermissions: [[
            "@pollux.manage.location.type"
          ]]
        },
      },
      {
        path: 'locations/branches',
        name: 'Location Branches',
        component: LocationBranches,
        meta: {
          title: 'Location Branches',
          salesPage: 'https://pollux.net/modules/#locations',
          modulePermissions: [[
            "LOCATION",
            "LOCATION_BRANCH",
          ]],
          userPermissions: [[
            "@pollux.view.location.branch"
          ]]
        },
      },

      {
        path: 'locations/branches/new',
        name: 'New Location Branch',
        component: NewLocationBranch,
        meta: {
          title: 'New Location Branch',
          salesPage: 'https://pollux.net/modules/#locations',
          modulePermissions: [[
            "LOCATION",
            "LOCATION_BRANCH:CREATE",
          ]],
          userPermissions: [[
            "@pollux.manage.location.branch"
          ]]
        },
      },
      {
        path: 'locations/bind',
        name: 'Bind Location',
        component: LocationNetworkBind,
        meta: {
          title: 'Connect a Location',
          salesPage: 'https://pollux.net/modules/#locations',
          modulePermissions: [[
            'LOCATION:LIST,UPDATE',
            'MERAKI_MAIN:LIST,UPDATE',
          ]],
          userPermissions: [[
            "@pollux.manage.location.location",
            "@pollux.manage.meraki.network"
          ]]
        },
      },
      {
        path: 'locations/:id',
        name: 'Location Detail',
        component: LocationDetail,
        meta: {
          title: 'View Location',
          salesPage: 'https://pollux.net/modules/#locations',
          disableScrollTop: true,
          modulePermissions: [[
            "LOCATION",
          ]],
          userPermissions: [[
            "@pollux.view.location.overview"
          ]]
        },
      },

      {
        path: 'locations/worldmap',
        name: 'Location Worldmap',
        redirect: { name: 'Location Overview WorldMap' },
      },
      {
        path: 'locations/worldmap/overview',
        name: 'Location Overview Worldmap',
        redirect: { name: 'Fullscreen Location Overview WorldMap' },
      },
      {
        path: 'locations/worldmap/overview/fullscreen',
        name: 'Fullscreen Location Overview WorldMap',
        component: WorldMapLocationOverviewFullscreen,
        meta: {
          title: 'Location Overview Worldmap Fullscreen',
          salesPage: 'https://pollux.net/modules/#locations',
          modulePermissions: [[
            "LOCATION",
          ]],
          userPermissions: [[
            "@pollux.view.location.overview"
          ]]
        },
      },

      {
        path: 'locations/speedtests',
        name: 'Location Speedtests',
        component: Speedtests,
        meta: {
          title: 'Location Speedtests',
          salesPage: 'https://pollux.net/modules/#locations',
          modulePermissions: [[
            "LOCATION",
            'SPEEDTEST',
          ]],
          userPermissions: [[
            "@pollux.view.location.speedtest"
          ]]
        },
      },

      {
        path: 'notification/:id',
        name: 'View notification',
        component: Notification,
        meta: {
          title: 'View Notification',
        }
      },
      {
        path: 'notification',
        name: 'notification',
        redirect: { name: 'Home' },
      },


      {
        path: 'meraki',
        name: 'meraki',
        component: MerakiDashboard,
        meta: {
          title: 'Meraki Dashboard',
          salesPage: 'https://pollux.net/modules/#meraki',
          modulePermissions: [[
            "MERAKI_MAIN",
          ]],
          userPermissions: [
            ...bundleShortcuts.merakiDeviceAny,
            ['@pollux.view.meraki.client'],
            ['@pollux.view.meraki.internet_connections'],
            ['@pollux.view.meraki.vpn_status'],
            ['@pollux.view.meraki.cellular_backup'],
            ['@pollux.manage.meraki.camera.request_footage'],
            ['@pollux.view.meraki.network.vlan'],
            ['@pollux.view.meraki.network'],
          ]
        },
      },
      {
        path: 'meraki/devices',
        name: 'Meraki Devices',
        component: Devices,
        meta: {
          title: 'Meraki Devices',
          salesPage: 'https://pollux.net/modules/#meraki',
          modulePermissions: [[
            "MERAKI_MAIN",
          ]],
          userPermissions: [
            ['@pollux.view.meraki.device.all'],
            ['@pollux.view.meraki.device.router'],
            ['@pollux.view.meraki.device.switch'],
            ['@pollux.view.meraki.device.wireless_lan'],
            ['@pollux.view.meraki.device.sensor'],
            ['@pollux.view.meraki.device.cellular_gateway'],
            ['@pollux.view.meraki.device.camera'],
          ]
        },
      },
      {
        path: 'meraki/clients',
        name: 'Meraki Connected Clients',
        component: Clients,
        meta: {
          title: 'Meraki Clients',
          salesPage: 'https://pollux.net/modules/#meraki',
          modulePermissions: [[
            "MERAKI_MAIN",
            "MERAKI_CLIENTS"
          ]],
          userPermissions: [[
            "@pollux.view.meraki.client"
          ]]
        },
      },
      {
        path: 'meraki/internet-connections',
        name: 'Meraki Internet Connections',
        component: InternetConnections,
        meta: {
          title: 'Meraki Internet Connections',
          salesPage: 'https://pollux.net/modules/#meraki',
          modulePermissions: [[
            "MERAKI_MAIN",
          ]],
          userPermissions: [[
            "@pollux.view.meraki.internet_connections"
          ]]
        },
      },
      {
        path: 'meraki/vpn-status',
        name: 'Meraki VPN Status',
        component: VPNStatus,
        meta: {
          title: 'Meraki VPN Status',
          salesPage: 'https://pollux.net/modules/#meraki',
          modulePermissions: [[
            "MERAKI_MAIN",
            "VPN_STATUS",
          ]],
          userPermissions: [[
            "@pollux.view.meraki.vpn_status"
          ]]
        },
      },
      {
        path: 'meraki/cameras/request-access',
        name: 'Meraki Request Camera Access',
        component: RequestCameraAccess,
        meta: {
          title: 'Meraki Request Camera Access',
          salesPage: 'https://pollux.net/modules/#meraki',
          userPermissions: [[
            "@pollux.manage.meraki.camera.request_footage"
          ]],
          modulePermissions: [[
            "MERAKI_MAIN",
            "LOCATION",
            "MERAKI_CAMERA_ACCESS:CREATE",
          ]],
        },
      },
      {
        path: 'meraki/cameras',
        name: 'Meraki Cameras',
        redirect: { name: 'Request Camera Access' }
      },
      {
        path: 'meraki/subnets',
        name: 'Meraki Subnet Information',
        component: SubnetInformation,
        meta: {
          title: 'Meraki Subnets',
          salesPage: 'https://pollux.net/modules/#meraki',
          modulePermissions: [[
            "MERAKI_MAIN",
          ]],
          userPermissions: [["@pollux.view.meraki.subnet"]]
        },
      },
      {
        path: 'meraki/subnets/import',
        name: 'Meraki Import Subnet',
        component: ImportSubnets,
        meta: {
          title: 'Import Meraki Subnets',
          salesPage: 'https://pollux.net/modules/#meraki',
          modulePermissions: [[
            "MERAKI_MAIN",
            'MERAKI_SUBNET_IMPORT',
          ]],
          userPermissions: [[
            '@pollux.manage.meraki.subnet'
          ]]
        },
      },
      {
        path: 'meraki/cellular-backup',
        name: 'Meraki Cellular Backup',
        component: MerakiKPN,
        meta: {
          title: 'Meraki Cellular Backup',
          salesPage: 'https://pollux.net/modules/#meraki',
          modulePermissions: [[
            "MERAKI_MAIN",
            "MERAKI_CELLULAR_BACKUP",
            "KPN_MAIN",
          ]],
          userPermissions: [[
            '@pollux.view.meraki.cellular_backup',
          ]]
        },
      },
      {
        path: 'meraki/networks',
        name: 'Meraki Networks',
        component: Networks,
        meta: {
          title: 'Meraki Networks',
          salesPage: 'https://pollux.net/modules/#meraki',
          modulePermissions: [[
            "MERAKI_MAIN",
          ]],
          userPermissions: [[
            "@pollux.view.meraki.network"
          ]]
        },
      },
      {
        path: 'meraki/networks/new',
        name: 'Meraki Create New Network',
        component: NewNetwork,
        meta: {
          title: 'Meraki Create New Network',
          salesPage: 'https://pollux.net/modules/#meraki',
          modulePermissions: [
            [
              "MERAKI_MAIN:CREATE",
              "BLUEPRINTS"
            ],
            [
              "MERAKI_MAIN:CREATE",
              "MERAKI_TEMPLATES"
            ]
          ],
          userPermissions: [[
            "@pollux.manage.meraki.network"
          ]]
        },
      },
      {
        path: 'meraki/networks/bind',
        name: 'Meraki Bind Network',
        component: LocationNetworkBind,
        meta: {
          title: 'Meraki Connect Network',
          salesPage: 'https://pollux.net/modules/#meraki',
          modulePermissions: [[
            "MERAKI_MAIN:LIST,UPDATE",
            "LOCATION:LIST,UPDATE",
          ]],
          userPermissions: [[
            "@pollux.manage.meraki.network",
            "@pollux.manage.location.location"
          ]]
        },
      },
      {
        path: 'meraki/networks/:id',
        name: 'Meraki Network Detail',
        component: NetworkDetail,
        meta: {
          title: 'Meraki View Network',
          salesPage: 'https://pollux.net/modules/#meraki',
          disableScrollTop: true,
          modulePermissions: [[
            "MERAKI_MAIN",
          ]],
          userPermissions: [[
            "@pollux.view.meraki.network"
          ]]
        },
      },
      {
        path: 'meraki/worldmap',
        name: 'Meraki WorldMap',
        redirect: { name: 'VPN Status WorldMap' },
      },
      {
        path: 'meraki/worldmap/status',
        name: 'VPN Status WorldMap',
        component: WorldMapVPNStatus,
        meta: {
          title: 'Meraki VPN Status Worldmap',
          salesPage: 'https://pollux.net/modules/#meraki',
          modulePermissions: [[
            "MERAKI_MAIN",
            "VPN_STATUS",
          ]],
          userPermissions: [[
            "@pollux.view.meraki.vpn_status"
          ]]
        }
      },
      {
        path: 'meraki/worldmap/status/fullscreen',
        name: 'Fullscreen VPN Status WorldMap',
        component: WorldMapVPNStatusFullscreen,
        meta: {
          title: 'Meraki Fullscreen VPN Status Worldmap',
          salesPage: 'https://pollux.net/modules/#meraki',
          modulePermissions: [[
            "MERAKI_MAIN",
            "VPN_STATUS",
          ]],
          userPermissions: [[
            "@pollux.view.meraki.vpn_status"
          ]]
        }
      },
      {
        path: 'meraki/switchports',
        name: 'Switchports',
        component: Switchports,
        meta: {
          title: 'Switchports Templates',
          salesPage: 'https://pollux.net/modules/#meraki',
          modulePermissions: [[
            "MERAKI_MAIN",
            "MERAKI_SWITCHPORTS",
          ]],
          userPermissions: [[
            "@pollux.view.meraki.switchports"
          ]]
        }
      },
      {
        path: 'meraki/mxports',
        name: 'MXPorts',
        component: MXPorts,
        meta: {
          title: 'MX Port Templates',
          salesPage: 'https://pollux.net/modules/#meraki',
          modulePermissions: [[
            "MERAKI_MAIN",
            "MERAKI_MXPORTS",
          ]],
          userPermissions: [[
            "@pollux.view.meraki.mxports"
          ]]
        }
      },

      {
        path: 'external-tools/sim-cards',
        name: 'Sim Cards Overview',
        component: SimCards,
        meta: {
          title: 'Sim card management',
          salesPage: 'https://pollux.net/modules/#simcards',
          modulePermissions: [[
            "MODULE_EXT_TOOLS_SIM_CARDS",
          ]],
          userPermissions: [
            ['@pollux.view.external_tools.sim_cards'],
          ]
        }
      },

      {
        path: 'support-desk',
        name: 'Support Desk',
        component: SupportDesk,
        meta: {
          title: 'Support Desk Dashboard',
          salesPage: 'https://pollux.net/modules/#support',
          modulePermissions: [[
            "SUPPORT_DESK",
          ]],
          userPermissions: [
            ['@pollux.view.support_desk.tickets'],
            ['@pollux.view.support_desk.changes'],
            ['@pollux.view.support_desk.service_reports'],
          ]
        }
      },
      {
        path: 'support-desk/tickets/all',
        name: 'Support Desk All Tickets',
        component: AllTickets,
        meta: {
          title: 'Support Desk - Tickets',
          salesPage: 'https://pollux.net/modules/#support',
          modulePermissions: [[
            "SUPPORT_DESK",
            "SUPPORT_DESK_TICKETS",
          ]],
          userPermissions: [
            ['@pollux.view.support_desk.tickets'],
          ]
        }
      },
      {
        path: 'support-desk/tickets',
        name: 'Support Desk Tickets',
        redirect: { name: 'Support Desk' }
      },
      {
        path: 'support-desk/changes',
        name: 'Support Desk Changes',
        component: ChangesCalendar,
        meta: {
          title: 'Support Desk - Changes',
          salesPage: 'https://pollux.net/modules/#support',
          modulePermissions: [[
            "SUPPORT_DESK",
            "SUPPORT_DESK_TICKETS",
          ]],
          userPermissions: [
            ['@pollux.view.support_desk.changes'],
          ]
        }
      },
      {
        path: 'support-desk/service-reports',
        name: 'Support Desk Service Reports',
        component: ServiceReports,
        meta: {
          title: 'Support Desk - Service Reports',
          salesPage: 'https://pollux.net/modules/#support',
          modulePermissions: [[
            "SUPPORT_DESK",
            "SERVICE_REPORTS",
          ]],
          userPermissions: [
            ['@pollux.view.support_desk.service_reports'],
          ]
        }
      },
      {
        path: 'support-desk/service-reports/new',
        name: 'New Service Report',
        component: NewServiceReport,
        meta: {
          title: 'New Service Report',
          salesPage: 'https://pollux.net/modules/#support',
          modulePermissions: [[
            "SUPPORT_DESK",
            "SERVICE_REPORTS:CREATE"
          ]],
          userPermissions: [[
            "@pollux.manage.support_desk.service_reports"
          ]]
        }
      },
      {
        path: 'support-desk/service-reports/:id',
        name: 'Support Desk Service Report',
        component: ServiceReport,
        meta: {
          title: 'Support Desk - View Service Report',
          salesPage: 'https://pollux.net/modules/#support',
          modulePermissions: [[
            "SUPPORT_DESK",
            "SERVICE_REPORTS",
          ]],
          userPermissions: [
            ['@pollux.view.support_desk.service_reports'],
          ]
        }
      },

      {
        path: 'about',
        name: 'About Pollux',
        component: About,
        meta: {
          title: 'About Pollux',
        }
      },
      {
        path: 'about/changelogs',
        name: 'About Changelogs',
        component: Changelogs,
        meta: {
          title: 'Pollux Changelogs',
        }
      },
      {
        path: '/about/changelogs/:version',
        name: 'About View Changelog',
        component: ChangeLog,
        meta: {
          title: 'Pollux View Changelog',
        }
      },
      {
        path: 'about/licenses',
        name: 'About Licenses',
        component: Licenses,
        meta: {
          title: 'Pollux Licences',
        }
      },
      {
        path: 'about/logs',
        name: 'System Logs',
        component: Logs,
        meta: {
          title: 'System Logs',
          userPermissions: [[
            "@pollux.view.main.user"
          ]]
        }
      },

      {
        path: 'ipam',
        name: "IPAM",
        redirect: { name: "VLAN Blueprints" },
      },
      {
        path: 'ipam/blueprints',
        name: "VLAN Blueprints",
        redirect: { name: 'VLAN Blueprints Network Ranges' },
      },
      {
        path: 'ipam/blueprints/network-ranges',
        name: 'VLAN Blueprints Network Ranges',
        component: NetworkRanges,
        meta: {
          title: 'VLAN Blueprints - Network Ranges',
          modulePermissions: [[
            'BLUEPRINTS',
          ]],
          userPermissions: [[
            "@pollux.view.ipam_blueprint.network_range"
          ]]
        }
      },
      {
        path: 'ipam/blueprints/network-ranges/new',
        name: 'VLAN Blueprints New Network Range',
        component: NewNetworkRange,
        meta: {
          title: 'VLAN Blueprints - New Network Range',
          modulePermissions: [[
            'BLUEPRINTS:CREATE',
          ]],
          userPermissions: [[
            "@pollux.manage.ipam_blueprint.network_range"
          ]]
        }
      },
      {
        path: 'ipam/blueprints/vlan-rules',
        name: 'VLAN Blueprints VLAN Rules',
        component: VLANRules,
        meta: {
          title: 'VLAN Blueprints - VLAN Rules',
          modulePermissions: [[
            'BLUEPRINTS',
          ]],
          userPermissions: [[
            "@pollux.view.ipam_blueprint.vlan_rule"
          ]]
        }
      },
      {
        path: 'ipam/blueprints/vlan-rules/new',
        name: 'VLAN Blueprints New VLAN Rule',
        component: NewVLANRule,
        meta: {
          title: 'VLAN Blueprints - New VLAN Rule',
          modulePermissions: [[
            'BLUEPRINTS:CREATE',
          ]],
          userPermissions: [[
            "@pollux.manage.ipam_blueprint.vlan_rule"
          ]]
        }
      },
      {
        path: 'ipam/blueprints/vlan-rule-actions',
        name: 'VLAN Blueprints VLAN Rule Actions',
        component: VLANRuleActions,
        meta: {
          title: 'VLAN Blueprints - VLAN Rule Actions',
          modulePermissions: [[
            'BLUEPRINTS',
          ]],
          userPermissions: [[
            "@pollux.view.ipam_blueprint.vlan_rule_action"
          ]]
        }
      },
      {
        path: 'ipam/blueprints/vlan-rule-actions/new',
        name: 'VLAN Blueprints New VLAN Rule Action',
        component: NewVLANRuleAction,
        meta: {
          title: 'VLAN Blueprints - New VLAN Rule Action',
          modulePermissions: [[
            'BLUEPRINTS:CREATE',
          ]],
          userPermissions: [[
            "@pollux.manage.ipam_blueprint.vlan_rule_action"
          ]]
        }
      },
      {
        path: 'ipam/overview',
        name: 'IPAM Overview',
        component: Overview,
        meta: {
          title: 'IPAM Overview',
          salesPage: 'https://pollux.net/modules/#ipam',
          modulePermissions: [[
            'MERAKI_IPAM',
          ]],
          userPermissions: [[
            '@pollux.view.ipam',
          ]]
        }
      },

      {
        path: 'verkada',
        name: 'Verkada Dashboard',
        component: VerkadaDashboard,
        meta: {
          title: 'Verkada Dashboard',
          salesPage: 'https://pollux.net/modules/#verkada',
          modulePermissions: [[
            'VERKADA_MAIN',
          ]],
          userPermissions: [
            ['@pollux.view.verkada.cameras'],
            ['@pollux.view.verkada.access'],
            ['@pollux.view.verkada.sites'],
            ['@pollux.view.location.overview', '@pollux.manage.verkada.cameras.footage_request'],
          ]
        }
      },
      {
        path: 'verkada/cameras',
        name: 'Verkada Cameras',
        component: VerkadaCameras,
        meta: {
          title: 'Verkada Cameras',
          salesPage: 'https://pollux.net/modules/#verkada',
          modulePermissions: [[
            'VERKADA_MAIN',
            'VERKADA_CAMERA',
          ]],
          userPermissions: [
            ['@pollux.view.verkada.cameras'],
          ]
        }
      },
      {
        path: 'verkada/controller-groups',
        name: 'Verkada Controller Group',
        component: VerkadaControllerGroup,
        meta: {
          title: 'Verkada Access Control',
          salesPage: 'https://pollux.net/modules/#verkada',
          modulePermissions: [[
            'VERKADA_MAIN',
            'VERKADA_ACCESS',
          ]],
          userPermissions: [
            ['@pollux.view.verkada.access'],
          ]
        }
      },
      {
        path: 'verkada/sites',
        name: 'Verkada Sites',
        component: VerkadaSites,
        meta: {
          title: 'Verkada Sites',
          salesPage: 'https://pollux.net/modules/#verkada',
          modulePermissions: [[
            'VERKADA_MAIN',
          ]],
          userPermissions: [
            ['@pollux.view.verkada.sites'],
          ]
        }
      },
      {
        path: 'verkada/cameras/request-access',
        name: 'Verkada Request Camera Access',
        component: VerkadaRequestCameraAccess,
        meta: {
          title: 'Verkada Request Camera Access',
          salesPage: 'https://pollux.net/modules/#verkada',
          modulePermissions: [[
            'VERKADA_MAIN',
            'VERKADA_CAMERA',
            'LOCATION:LIST',
          ]],
          userPermissions: [
            [
              '@pollux.view.location.overview',
              '@pollux.manage.verkada.cameras.footage_request',
            ],
          ]
        }
      },
      {
        path: 'verkada/sites/bind',
        name: 'Connect a Verkada site',
        component: VerkadaLocationSiteBind,
        meta: {
          title: 'Connect a Verkada site',
          salesPage: 'https://pollux.net/modules/#verkada',
          modulePermissions: [[
            'VERKADA_MAIN:LIST,UPDATE',
            'LOCATION:LIST,UPDATE'
          ]],
          userPermissions: [[
            '@pollux.view.verkada.sites',
            '@pollux.manage.verkada.sites',
            '@pollux.view.location.overview',
            '@pollux.manage.location.location',
          ],]
        }
      },

      {
        path: 'wyebot',
        name: 'Wyebot Dashboard',
        component: WyebotDashboard,
        meta: {
          title: 'Wyebot Dashboard',
          salesPage: 'https://pollux.net/modules/#wyebot',
          modulePermissions: [[
            'WYEBOT_MAIN',
          ]],
          userPermissions: [
            ['@pollux.view.wyebot.sites'],
            ['@pollux.view.wyebot.sensors'],
            ['@pollux.view.wyebot.tests'],
          ]
        }
      },
      {
        path: 'wyebot/sensors',
        name: 'Wyebot Sensors',
        component: WyebotSensors,
        meta: {
          title: 'Wyebot Sensors',
          salesPage: 'https://pollux.net/modules/#wyebot',
          modulePermissions: [[
            'WYEBOT_MAIN',
            'WYEBOT_SENSORS',
          ]],
          userPermissions: [
            ['@pollux.view.wyebot.sensors'],
          ]
        }
      },
      {
        path: 'wyebot/sites',
        name: 'Wyebot Sites',
        component: WyebotSites,
        meta: {
          title: 'Wyebot Sites',
          salesPage: 'https://pollux.net/modules/#wyebot',
          modulePermissions: [[
            'WYEBOT_MAIN',
          ]],
          userPermissions: [
            ['@pollux.view.wyebot.sites'],
          ]
        }
      },
      {
        path: 'wyebot/sites/bind',
        name: 'Connect a Wyebot site',
        component: WyebotLocationSiteBind,
        meta: {
          title: 'Connect a Wyebot site',
          salesPage: 'https://pollux.net/modules/#wyebot',
          modulePermissions: [[
            'WYEBOT_MAIN:LIST,UPDATE',
            'LOCATION:LIST,UPDATE'
          ]],
          userPermissions: [[
            '@pollux.view.wyebot.sites',
            '@pollux.manage.wyebot.sites',
            '@pollux.view.location.overview',
            '@pollux.manage.location.location',
          ]]
        }
      },
      {
        path: 'wyebot/sensors/bind',
        name: 'Connect a Wyebot sensor',
        component: WyebotLocationSensorBind,
        meta: {
          title: 'Connect a Wyebot sensor',
          salesPage: 'https://pollux.net/modules/#wyebot',
          modulePermissions: [[
            'WYEBOT_MAIN',
            'WYEBOT_SENSORS:LIST,UPDATE',
            'LOCATION:LIST,UPDATE'
          ]],
          userPermissions: [[
            '@pollux.view.wyebot.sensors',
            '@pollux.manage.wyebot.sensors',
            '@pollux.view.location.overview',
            '@pollux.manage.location.location',
          ]]
        }
      },
      {
        path: 'wyebot/tests',
        name: 'Wyebot Tests',
        component: WyebotTests,
        meta: {
          title: 'Wyebot Tests',
          salesPage: 'https://pollux.net/modules/#wyebot',
          modulePermissions: [[
            'WYEBOT_MAIN',
            'WYEBOT_TESTS',
          ]],
          userPermissions: [[
            '@pollux.view.wyebot.tests',
          ]]
        }
      },
      {
        path: 'wyebot/tests/create',
        name: 'Create a Wyebot Test',
        component: WyebotCreateNewTest,
        meta: {
          title: 'Create a Wyebot Test',
          salesPage: 'https://pollux.net/modules/#wyebot',
          modulePermissions: [[
            'WYEBOT_MAIN',
            'WYEBOT_TESTS:CREATE',
          ]],
          userPermissions: [[
            '@pollux.view.wyebot.tests',
            '@pollux.manage.wyebot.tests',
          ]]
        }
      },
      {
        path: 'documentation',
        name: 'Dashboard',
        component: DocumentationDashboard,
        meta: {
          title: 'Documentation',
          modulePermissions: [[
              'LOCATION_DOCUMENTATION'
          ],[
              'PROCEDURE_DOCUMENTATION'
          ],[
              'SERVICE_REPORTS'
          ]],
          userPermissions: [[
            '@pollux.view.location.documentation',
          ],[
            '@pollux.view.documentation.procedures',
          ],[
            '@pollux.view.support_desk.service_reports',
          ]]
        }
      },
      {
        path: 'documentation/locations',
        name: 'Location Documentations',
        component: LocationsDocumentation,
        meta: {
          title: 'Location Documentation',
          modulePermissions: [[
              'LOCATION_DOCUMENTATION'
          ]],
          userPermissions: [[
              '@pollux.view.location.documentation'
          ]]
        }
      },
      {
        path: 'documentation/procedures',
        name: 'Procedure Documentation',
        component: DocumentationProcedures,
        meta: {
          title: 'Procedure Documentation',
          modulePermissions: [[
              'PROCEDURE_DOCUMENTATION'
          ]],
          userPermissions: [[
              '@pollux.view.documentation.procedures',
          ]]
        }
      },
      {
        path: 'documentation/procedures/new',
        name: 'New Procedure Documentation',
        component: NewProcedureDocumentation,
        meta: {
          title: 'New Procedure Documentation',
          modulePermissions: [[
            "PROCEDURE_DOCUMENTATION:CREATE"
          ]],
          userPermissions: [[
            '@pollux.manage.documentation.procedures',
          ]]
        },
      },
      {
        path: 'documentation/procedures/:id',
        name: 'View Procedure Documentation',
        component: ProcedureDocumentation,
        meta: {
          title: 'View Procedure Documentation',
          modulePermissions: [[
            "PROCEDURE_DOCUMENTATION",
          ]],
          userPermissions: [[
            '@pollux.view.documentation.procedures',
          ]]
        },
      },
      {
        path: 'documentation/service-reports',
        name: 'Service Reports',
        component: ServiceReports,
        meta: {
          title: 'Service Rapportage',
          modulePermissions: [[
              'SERVICE_REPORTS'
          ]],
          userPermissions: [[
              '@pollux.view.support_desk.service_reports',
          ]]
        }
      },

      // Keep these redirects as these urls might still be reffered to by users of the old version
      {
        path: 'knowledgebase',
        component: NoComponent,
        beforeEnter() { window.open('https://documentation.pollux.net', '_self') }
      },
      {
        path: 'kb',
        component: NoComponent,
        beforeEnter() { window.open('https://documentation.pollux.net', '_self') }
      },

      {
        path: 'settings/',
        name: 'SettingsWrapper',
        component: Settings,
        children: [
          {
            path: '',
            name: 'Settings',
            component: SettingsDashboard,
            meta: {
              title: 'Settings Dashboard',
            }
          },
          {
            path: 'account/security',
            name: 'Account Settings - Account Security',
            component: AccountSecurity,
            meta: {
              title: 'Account Security Settings',
            }
          },
          {
            path: 'account/profile',
            name: 'Account Settings - Profile',
            component: Profile,
            meta: {
              title: 'Profile Settings',
            }
          },
          {
            path: 'account',
            name: 'Account Settings',
            redirect: { name: 'Settings' }
          },
          {
            path: 'organization',
            name: 'Organization Settings',
            redirect: { name: 'Settings' }
          },
          {
            path: 'organization/general',
            name: 'Organization Settings - General',
            component: SettingsGeneral,
            meta: {
              title: 'General Organization Settings',
            }
          },
          {
            path: 'organization/team',
            name: 'Organization Settings - Team',
            component: Team,
            meta: {
              title: 'Your Team',
              userPermissions: [[
                "@pollux.view.main.user"
              ]]
            }
          },
          {
            path: 'organization/api-keys',
            name: 'Organization Settings - API Keys',
            component: APIKeys,
            meta: {
              title: 'Development API Keys',
              modulePermissions: [[
                'LOCATION',
                'SPEEDTEST',
              ]],
              userPermissions: [[
                '@pollux.view.location.speedtest'
              ]]
            },
          },
          {
            path: 'organization/team/invitations',
            name: 'Organization Settings - Invitations',
            component: Invitations,
            meta: {
              title: 'Your Invitations',
              userPermissions: [[
                "@pollux.view.main.invitation"
              ]]
            }
          },
          {
            path: 'organization/security/magic-links',
            name: 'Organization Settings - Magic Links',
            component: MagicLinks,
            meta: {
              title: 'Magic Links',
              modulePermissions: [[
                'MAGICLINKS',
              ]],
              userPermissions: [[
                "@pollux.view.security.magic_link"
              ], [
                "@pollux.manage.security.magic_link"
              ]]
            }
          },
          {
            path: 'organization/team/edit-user',
            name: 'Organization Settings - Team - Edit User Redirect',
            redirect: { name: 'Organization Settings - Team' }

          },
          {
            path: 'organization/team/edit-user/:id',
            name: 'Organization Settings - Team - Edit User',
            component: EditUser,
            meta: {
              title: 'Edit User',
              userPermissions: [[
                "@pollux.view.main.user"
              ]]
            }
          },
          {
            path: 'organization/roles',
            name: 'Organization Settings - Roles',
            component: Roles,
            meta: {
              title: 'Roles',
              userPermissions: [[
                "@pollux.view.main.role"
              ]]
            }
          },
          {
            path: 'organization/roles/:id',
            name: 'Organization Settings - Edit Role',
            component: EditRole,
            meta: {
              title: 'Edit Role',
              userPermissions: [[
                "@pollux.view.main.role"
              ]]
            }
          },
          {
            path: 'organization/roles/new',
            name: 'Organization Settings - New Roles',
            component: EditRole,
            meta: {
              title: 'New Role',
              userPermissions: [[
                "@pollux.manage.main.role"
              ]]
            }
          },
          {
            path: 'organization/security',
            name: 'Organization Settings - Security',
            component: Security,
            meta: {
              title: 'Organization Security',
            }
          },
          {
            path: 'organization/integrations',
            name: 'Organization Settings - Integrations',
            component: Integrations,
            meta: {
              title: 'Integrations',
              userPermissions: [[
                "@pollux.view.main.organization.integration"
              ]]
            }
          },

          {
            path: 'support-desk',
            name: 'Support Desk Settings',
            redirect: { name: 'Settings' }
          },
          {
            path: 'support-desk/configuration',
            name: 'Support Desk Settings - Configuration',
            meta: {
              title: 'Support Desk Settings',
              modulePermissions: [[
                "SUPPORT_DESK",
                "SUPPORT_DESK_TICKETS",
              ]],
              userPermissions: [
                ["@pollux.view.support_desk.settings"],
                ["@pollux.manage.support_desk.settings"],
              ]
            },
            component: SupportDeskConfiguration,
          },

          {
            path: 'locations',
            name: 'Location Settings',
            redirect: { name: 'Settings' }
          },
          {
            path: 'locations/configuration',
            name: 'Location Settings - Configuration',
            meta: {
              title: 'Location Settings',
              modulePermissions: [[
                "LOCATION:UPDATE",
              ]],
              userPermissions: [[
                "@pollux.manage.main.organization.strategy.location.naming",
              ]]
            },
            component: Configuration,
          },
          {
            path: 'locations/granular-permissions',
            name: 'Location Settings - Granular Permissions',
            meta: {
              title: 'Location Settings',
              modulePermissions: [[
                "LOCATION",
              ]],
              userPermissions: [[
                "@pollux.manage.main.organization.strategy.location.naming",
              ]]
            },
            component: LocationGranularPermissions,
          },

          {
            path: 'meraki',
            name: 'Meraki Settings',
            redirect: { name: 'Settings' }
          },
          {
            path: 'meraki/guest-wi-fi',
            name: 'Meraki Settings - Guest Wifi',
            meta: {
              title: 'Meraki Guest Wi-Fi Settings',
              modulePermissions: [[
                "MERAKI_MAIN",
                "MERAKI_GUEST_WIFI:UPDATE",
              ]],
              userPermissions: [[
                "@pollux.manage.meraki.settings.guest_wifi",
              ]]
            },
            component: GuestWifi,
          },
          {
            path: 'meraki/networks',
            name: 'Meraki Settings - Networks',
            meta: {
              title: 'Meraki Network Settings',
              modulePermissions: [[
                "MERAKI_MAIN:UPDATE",
              ]],
              userPermissions: [[
                "@pollux.manage.main.organization.strategy.network.naming",
              ]]
            },
            component: NetworkSettings,
          },


          {
            path: 'billing',
            name: 'Billing Settings',
            redirect: { name: 'Settings' }
          },
          {
            path: 'billing/upgrade',
            name: 'Billing Settings - Upgrade',
            meta: {
              title: 'Upgrade Your Plan',
            },
            component: Upgrade,
          },
          {
            path: 'billing/your-plan',
            name: 'Billing Settings - Your Plan',
            component: YourPlan,
            meta: {
              title: 'Your Plan',
            },
          },
          {
            path: 'billing/invoices',
            name: 'Billing Settings - Invoices',
            component: Invoices,
            meta: {
              title: 'Invoices',
            },
          },
          {
            path: 'billing/details',
            name: 'Billing Settings - Billing Details',
            component: BillingDetails,
            meta: {
              title: 'Billing Details',
            },
          },
        ],
      },

      { path: '/auth/sign-out', name: 'Auth Sign out', component: Logout },

      { path: '/:pathMatch(.*)*', name: 'Error 404', component: E404 },

    ]
  },
  {
    path: '/onboarding',
    component: OnboardingLayout,
    beforeEnter: checkHostname,
    meta: {
      title: 'Onboarding',
      routeType: 'onboarding',
    },
    children: [
      { path: 'organization', component: OnboardingHome },
      { path: 'sign-out', name: 'Onboarding Sign out', component: Logout },
    ]
  },
  {
    path: '/superuser',
    component: SuperUserLayout,
    beforeEnter: checkAdminRights,
    meta: {
      title: 'Super User',
    },
    children: [
      { path: '', component: SuperUserHome },
      { path: 'create-organization', component: SuperUserCreateOrganization },
      { path: 'new-user', component: SuperUserCreateUser },
      { path: 'sign-out', name: 'Sign out', component: Logout },
    ]
  },
  {
    path: '/auth',
    component: AuthLayout,
    children: [
      {
        path: '',
        redirect: '/auth/sign-in'
      },
      {
        path: 'sign-in',
        component: Login,
        meta: {
          title: 'Sign In',
        },
      },
      {
        path: 'sign-up',
        name: 'sign-up',
        component: Register,
        meta: {
          title: 'Sign Up',
        },
      },
      {
        path: 'sign-in/trouble',
        component: Trouble,
        meta: {
          title: 'Trouble Logging In',
        },
      },
      {
        path: 'sign-in/trouble/forgot-email',
        component: ForgotEmail,
        meta: {
          title: 'Forgot Email',
        },
      },
      {
        path: 'sign-in/trouble/forgot-password',
        component: ForgotPassword,
        meta: {
          title: 'Forgot Password',
        },
      },
      {
        path: 'reset-password',
        component: ResetPassword,
        meta: {
          title: 'Reset Password',
        },
      },
      {
        path: 'sign-in/microsoft',
        redirect: 'auth/sign-in',
        meta: {
          title: 'Sign In',
        },
      },
      {
        path: 'sign-in/microsoft/callback',
        component: MicrosoftCallback,
        meta: {
          title: 'Sign In',
        },
      },
    ]
  }
]

/**
 * We create the router using the History Mode (no anchors).
 * We scroll to the top of a page if it is a new page
 * otherwise we stay on the original position
 */
const router = createRouter({
  history: createWebHistory(),
  routes,
  scrollBehavior(to, from, savedPosition) {
    if (to.path == from.path) {
      return savedPosition
    }
    return { left: 0, top: 0 }
  }
} as any)

/**
 * Here we define the middleware that is being triggered before
 * changing to a different route
 */
router.beforeEach(async (to, from) => {
  // redirect to login page if not logged in and trying to access a restricted page
  const publicPages = ['/auth/sign-in', '/auth/register'];
  const authRequired = !publicPages.includes(to.path);
  const authStore = useAuthStore();
  const apiStore = useAPIStore();
  const uiStore = useUIStore()
  const syncStore = useSyncStore()

  // We set the title of the page
  if (to.meta && to.meta.title) {
    document.title = `${to.meta.title} - Pollux.net`
  } else {
    document.title = "Pollux.net - By Veducon"
  }

  // The new data must be set to false as we are going to a different page
  // with different data
  syncStore.newData = false

  // Block navigation if there are unsaved changes — but only when actually
  // leaving the page. Query-only changes (e.g. SearchExpandable syncing
  // `?<name>__q=` on every keystroke) keep the same path, so they must not
  // trigger the unsaved-changes prompt.
  if (uiStore.unsavedChanges && to.path !== from.path) {
    uiStore.unsavedChangesProtectedURL = to
    uiStore.showUnsavedChangesProtection = true
    return false
  }

  uiStore.disablePermissionsConfirmPopup()

  // Redirect to the login page if page requires authentication and you are not authenticated
  if (authRequired && !authStore.user) {
    if (to.fullPath == "/") {
      authStore.returnUrl = to.fullPath;
      return '/auth/sign-in';
    }
  }

  // Always get organization settings when navigating to an authenticated page
  if (authStore.user && !authStore.isPublicDomain() && from.path != to.path) {
    apiStore.getOrganizationSettings()
    setTimeout(() => apiStore.getMessages(), 1)
  }

  // Only redirect to onboarding for Organizations
  if (!authStore.isPublicDomain() && apiStore.organizationSettings) {
    // If Organization has not finished onboarding
    // and route is not onboarding then redirect the user
    // to finish onboarding
    if (!apiStore.organizationSettings.onboarding_complete && to.meta.routeType !== 'onboarding' && authStore.user) {
      const org_settings = await apiStore.getOrganizationSettings()
      if (!apiStore.organizationSettings.onboarding_complete) {
        return "/onboarding/organization"
      }

    }

    // If Organization has finalized onboarding
    // and user is trying to go to onboarding
    // redirect to Homepage
    if (apiStore.organizationSettings.onboarding_complete && to.meta.routeType === "onboarding") {
      return "/"
    }
  }

})

export default router
